/**
 * Switching org and entering or leaving a client preview (SPEC decision 22): remember the choice (api/orgSelection.ts),
 * drop every cached answer of the previous org so nothing of it shows in the next one, and start from a page that
 * exists in every org.
 */
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { joinInvitation } from '@/api/endpointsMembers';
import { showMutationError } from '@/lib/mutationError';
import { queryClient } from '@/api/queryClient';
import { selectOrg, setPreviewTarget, type PreviewTarget } from '@/api/orgSelection';

export function useOrgActions() {
  const navigate = useNavigate();
  const restart = (to: '/' | '/clients') => {
    // Drop what nobody shows, and reload what is on screen: `clear()` alone would leave mounted views waiting forever.
    queryClient.removeQueries({ type: 'inactive' });
    void queryClient.resetQueries({ type: 'active' });
    void navigate({ to });
  };
  const switchOrg = (orgId: string) => { selectOrg(orgId); restart('/'); };
  return {
    switchOrg,
    /** Accepts an invitation from the switcher, then opens the org just joined. */
    joinOrg: async (invitationId: string): Promise<void> => {
      try {
        const joined = await joinInvitation(invitationId);
        toast.success(`You joined ${joined.org.name}`);
        switchOrg(joined.org.id);
      } catch (e) {
        showMutationError(e, 'Could not join the organization');
      }
    },
    startPreview: (target: PreviewTarget) => { setPreviewTarget(target); restart('/'); },
    exitPreview: () => { setPreviewTarget(null); restart('/clients'); },
  };
}

/** The last view an operator was in, so Tutorials keeps it; module state: it lasts for the tab, like the router. */
let lastView: 'admin' | 'workspace' = 'workspace';
export const rememberView = (view: 'admin' | 'workspace'): 'admin' | 'workspace' => (lastView = view);
export const previousView = (): 'admin' | 'workspace' => lastView;
